import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { BRAND } from "../data/content";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
    <defs>
      <linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#FEDA75" />
        <stop offset="25%" stopColor="#FA7E1E" />
        <stop offset="50%" stopColor="#D62976" />
        <stop offset="75%" stopColor="#962FBF" />
        <stop offset="100%" stopColor="#4F5BD5" />
      </linearGradient>
    </defs>
    <rect width="24" height="24" rx="6" fill="url(#ig-grad)" />
    <rect x="5" y="5" width="14" height="14" rx="4" fill="none" stroke="#fff" strokeWidth="1.7" />
    <circle cx="12" cy="12" r="3.4" fill="none" stroke="#fff" strokeWidth="1.7" />
    <circle cx="16.9" cy="7.1" r="1.1" fill="#fff" />
  </svg>
);

const LinkedInIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
    <rect width="24" height="24" rx="4" fill="#0A66C2" />
    <path
      fill="#fff"
      d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z"
    />
  </svg>
);

const SOCIAL_ICONS = { instagram: InstagramIcon, linkedin: LinkedInIcon };

const PROJECT_TYPES = [
  "Strategy & Creative",
  "Brand Activation & Events",
  "Tech-led & Hybrid",
  "Live IPs & Entertainment",
  "Production & Fabrication",
  "Corporate & Government Events",
  "Digital Amplification & Content",
  "Others",
];

const BUDGETS = ["< $50k", "$50–150k", "$150–500k", "$500k+"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_type: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and a message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message received. We'll be in touch within 48 hours.");
      setForm({
        name: "",
        email: "",
        company: "",
        project_type: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please retry.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative shell pt-10 md:pt-16 pb-16 md:pb-20 bg-[color:var(--collab-forest)] text-[color:var(--collab-cream)]"
    >
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1, ease: EASE }}
        className="font-display font-light text-[11vw] md:text-[8vw] lg:text-[7.2vw] leading-[0.92] tracking-[-0.03em] text-[color:var(--collab-cream)]"
      >
        Let's build{" "}
        <em className="italic font-medium text-[color:var(--collab-lime)]">
          something
        </em>{" "}
        together.
      </motion.h2>

      <div className="mt-16 md:mt-20 grid grid-cols-12 gap-6 md:gap-10">
        {/* Left column — details */}
        <div className="col-span-12 md:col-span-4">
          <div className="space-y-8 [&_.label-mono]:!text-[color:var(--collab-cream)]/70">
            <div>
              <div className="label-mono">Email</div>
              <a
                data-testid="contact-email"
                href={`mailto:${BRAND.email}`}
                className="mt-2 block font-display text-2xl md:text-3xl text-[color:var(--collab-cream)] hover:text-[color:var(--collab-lime)] transition-colors"
              >
                {BRAND.email}
              </a>
            </div>
            <div>
              <div className="label-mono">Phone</div>
              <div className="mt-2 font-display text-2xl">{BRAND.phone}</div>
            </div>
            <div>
              <div className="label-mono">Follow</div>
              <div className="mt-2 flex flex-col gap-2">
                {BRAND.socials.map((s) => {
                  const Icon = SOCIAL_ICONS[s.label.toLowerCase()];
                  return (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-2.5 font-sans text-sm hover:text-[color:var(--collab-lime)] transition-colors"
                      data-testid={`contact-social-${s.label.toLowerCase()}`}
                    >
                      {Icon && <Icon aria-hidden />}
                      {s.label} →
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Right column — form */}
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="col-span-12 md:col-span-8"
          noValidate
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            <FieldWrap label="Name" required>
              <input
                type="text"
                className="field"
                placeholder="Your full name"
                value={form.name}
                onChange={update("name")}
                required
                data-testid="contact-input-name"
              />
            </FieldWrap>

            <FieldWrap label="Email" required>
              <input
                type="email"
                className="field"
                placeholder="you@company.com"
                value={form.email}
                onChange={update("email")}
                required
                data-testid="contact-input-email"
              />
            </FieldWrap>

            <FieldWrap label="Company">
              <input
                type="text"
                className="field"
                placeholder="Where you work"
                value={form.company}
                onChange={update("company")}
                data-testid="contact-input-company"
              />
            </FieldWrap>

            <FieldWrap label="Project type">
              <select
                className="field appearance-none"
                value={form.project_type}
                onChange={update("project_type")}
                data-testid="contact-input-project-type"
              >
                <option value="">Select a type</option>
                {PROJECT_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </FieldWrap>

            <FieldWrap label="Tell us about the project" required className="md:col-span-2">
              <textarea
                className="field resize-none"
                rows={5}
                placeholder="More details of the project"
                value={form.message}
                onChange={update("message")}
                required
                data-testid="contact-input-message"
              />
            </FieldWrap>
          </div>

          <div className="mt-10 flex items-center justify-end flex-wrap gap-6">
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 bg-[color:var(--collab-lime)] text-[color:var(--collab-ink)] px-6 py-3 font-mono text-[12px] uppercase tracking-[0.18em] border border-[color:var(--collab-lime)] hover:bg-[color:var(--collab-cream)] hover:border-[color:var(--collab-cream)] transition-colors disabled:opacity-50"
            >
              {loading ? "Sending…" : sent ? "Sent — thank you" : "Send brief"}
              <span aria-hidden>{sent ? "✓" : "→"}</span>
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function FieldWrap({ required, className = "", children }) {
  return (
    <div className={className}>
      <div className="[&_.field]:text-[color:var(--collab-cream)] [&_.field]:border-b-[rgba(245,241,232,0.28)] [&_.field:focus]:border-b-[color:var(--collab-lime)] [&_.field::placeholder]:text-[rgba(245,241,232,0.45)]">
        {children}
      </div>
    </div>
  );
}
